/* screens/academy/RetakeRequests.jsx — кураторская сторона ST-5: заявки учеников
   на пересдачу теста и сброс попыток.

   Зачем отдельный модуль: одно и то же действие нужно в трёх местах — вкладка
   «Пересдачи» в консоли (очередь), дровер проверки ДЗ (тот же ученик под рукой) и
   карточка ученика. Дублировать гард `quiz_passed` в трёх экранах — гарантия того,
   что в одном из них его забудут, и куратор упрётся в отказ без объяснения.

   Контракт строки очереди (backend views._retake_row):
     { id, enrollment_id, student_label, student_email, course_id, course_title,
       quiz_id, lesson_title, attempts_used, max_attempts, comment, created_at }
   PII: email приходит только методисту (чистому куратору — пустая строка), поэтому
   подпись всегда student_label, а почта рисуется лишь когда она реально пришла. */
import React from 'react';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { QueryBoundary, EmptyState } from '../../components/patterns/States.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { academyErrorText } from '../../lib/academy/errorCodes.js';
import { hooks } from '../../hooks/index.jsx';
import * as api from '../../lib/api/academy.js';

const { useState, useCallback } = React;
const { useFakeQuery } = hooks;

function fmt(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('ru-RU',
    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Сброс попыток теста с гардом «тест уже сдан».
 *  Бэк отвечает 400 code='quiz_passed', потому что сброс откатывает зачёт по
 *  уроку и статус курса. Спрашиваем подтверждение и повторяем с force=1 — та же
 *  схема, что у удаления контента с работами учеников (has_student_data). */
export function useQuizReset({ onDone } = {}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [busyKey, setBusyKey] = useState(null);

  const reset = useCallback(async (enrollmentId, quizId, { lessonTitle } = {}) => {
    const key = `${enrollmentId}:${quizId}`;
    setBusyKey(key);
    const done = (res) => {
      toast.show(`Попытки сброшены${res && res.deleted ? ` (удалено: ${res.deleted})` : ''}`,
        'success');
      onDone && onDone();
    };
    try {
      done(await api.resetQuizAttempts(enrollmentId, quizId));
      return true;
    } catch (e) {
      if (!api.isQuizPassedGuard(e)) {
        toast.show(academyErrorText(e, 'Не удалось сбросить попытки'), 'danger');
        return false;
      }
      const ok = await confirm({
        title: 'Тест уже сдан — сбросить всё равно?',
        body: `Сброс удалит зачёт${lessonTitle ? ` по уроку «${lessonTitle}»` : ''} `
          + 'и пересчитает прогресс курса. Ранее выданный сертификат останется действительным.',
        confirmLabel: 'Сбросить попытки', danger: true,
      });
      if (!ok) return false;
      try {
        done(await api.resetQuizAttempts(enrollmentId, quizId, true));
        return true;
      } catch (e2) {
        toast.show(academyErrorText(e2, 'Не удалось сбросить попытки'), 'danger');
        return false;
      }
    } finally { setBusyKey(null); }
  }, [toast, confirm, onDone]);

  // isBusy — по ПАРЕ (запись, тест): в карточке ученика кнопок сброса несколько,
  // и один общий флаг крутил бы спиннер сразу на всех.
  return { reset, isBusy: (enrollmentId, quizId) => busyKey === `${enrollmentId}:${quizId}` };
}

/** Одна заявка: кто, по какому уроку, сколько попыток сжёг и что просил. */
function RetakeRow({ row, onReset, onDecline, busy, showCourse = true }) {
  return (
    <div className="acad-retake" role="listitem">
      <span className="acad-retake__glyph"><Icon name="refresh" size={16} /></span>
      <div className="acad-retake__main">
        <span className="acad-retake__name">{row.student_label || 'Ученик'}</span>
        <span className="acad-retake__sub t-caption text-mute">
          {showCourse && <>{row.course_title} · </>}{row.lesson_title}
          {row.max_attempts > 0 && <> · попытки <span className="num">{row.attempts_used}/{row.max_attempts}</span></>}
        </span>
        {row.comment && <span className="acad-retake__comment">{row.comment}</span>}
      </div>
      <time className="acad-retake__time t-caption text-mute" dateTime={row.created_at}>{fmt(row.created_at)}</time>
      <div className="acad-retake__actions">
        <Button size="sm" variant="primary" iconLeft="refresh" loading={busy}
          onClick={() => onReset(row)}>Сбросить попытки</Button>
        <Button size="sm" variant="ghost" iconLeft="close" disabled={busy}
          onClick={() => onDecline(row)}>Отклонить</Button>
      </div>
    </div>
  );
}

/** Очередь заявок на пересдачу. `enrollmentId` — срез по одному ученику
 *  (дровер проверки/карточка): бэк фильтрует только по курсу, поэтому сужаем
 *  на клиенте. `embedded` — вложенный режим: при пустой очереди секция исчезает
 *  ЦЕЛИКОМ вместе с заголовком, иначе в дровере висел бы мёртвый заголовок. */
export function RetakeQueue({ courseId, enrollmentId, embedded = false, heading, onChanged }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const q = useFakeQuery(`academy-retakes-${courseId || 'all'}-${enrollmentId || 'all'}-${nonce}`,
    () => api.getRetakeRequests(courseId ? { course_id: courseId } : {}), [courseId, nonce]);
  const all = Array.isArray(q.data) ? q.data : [];
  const rows = enrollmentId
    ? all.filter((r) => String(r.enrollment_id) === String(enrollmentId))
    : all;

  const afterChange = useCallback(() => { reload(); onChanged && onChanged(); },
    [reload, onChanged]);
  const { reset, isBusy } = useQuizReset({ onDone: afterChange });

  const decline = useCallback(async (row) => {
    const ok = await confirm({
      title: 'Отклонить запрос на пересдачу?',
      body: 'Попытки останутся исчерпанными, заявка уйдёт из очереди. '
        + 'Ученик увидит это на экране теста.',
      confirmLabel: 'Отклонить',
    });
    if (!ok) return;
    try {
      await api.declineRetakeRequest(row.id);
      toast.show('Запрос отклонён', 'muted');
      afterChange();
    } catch (e) { toast.show(academyErrorText(e, 'Не удалось отклонить'), 'danger'); }
  }, [confirm, toast, afterChange]);

  // Внутри дровера пустая очередь — норма и не должна занимать экран.
  if (embedded && !q.loading && !q.error && !rows.length) return null;

  const list = (
    <div className="acad-retakes" role="list">
      {rows.map((r) => (
        <RetakeRow key={r.id} row={r} showCourse={!enrollmentId && !courseId}
          busy={isBusy(r.enrollment_id, r.quiz_id)}
          onReset={(row) => reset(row.enrollment_id, row.quiz_id, { lessonTitle: row.lesson_title })}
          onDecline={decline} />
      ))}
    </div>
  );

  return (
    <section className={embedded ? 'acad-section' : undefined}>
      {heading && (
        <div className="acad-section__head">
          <Icon name="refresh" size={15} /><span className="acad-section__title">{heading}</span>
        </div>
      )}
      <QueryBoundary loading={q.loading && !rows.length} error={q.error}
        empty={!rows.length} onRetry={reload}
        emptyEl={<EmptyState icon="check-circle" title="Заявок на пересдачу нет"
          body="Здесь появятся ученики, у которых закончились попытки теста." />}>
        {list}
      </QueryBoundary>
    </section>
  );
}
